import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import EmptyState from './EmptyState';
import { TableSkeleton } from './LoadingSkeleton';

/**
 * Enterprise-grade DataTable component
 * Provides sorting, pagination, selection, and row actions
 * Supports both standard and MUI DataGrid column formats
 *
 * @param {Object} props
 * @param {Array} props.columns - Column definitions
 *   Standard format: [{id, label, sortable, align, render}]
 *   MUI DataGrid format: [{field, headerName, sortable, align, renderCell}]
 * @param {Array} props.rows - Data rows
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.selectable - Enable row selection
 * @param {Array} props.selected - Selected row IDs
 * @param {Function} props.onSelectChange - Handler for selection changes
 * @param {Function} props.onRowClick - Handler for row clicks
 * @param {Array} props.rowActions - Row action buttons [{icon, label, onClick}]
 * @param {string} props.sortBy - Current sort column ID
 * @param {string} props.sortOrder - Sort order: 'asc' | 'desc'
 * @param {Function} props.onSortChange - Handler for sort changes
 * @param {number} props.page - Current page (0-indexed)
 * @param {number} props.rowsPerPage - Rows per page
 * @param {number} props.totalRows - Total number of rows
 * @param {Function} props.onPageChange - Handler for page changes
 * @param {Function} props.onRowsPerPageChange - Handler for rows per page changes
 * @param {string} props.emptyMessage - Custom empty message
 * @param {boolean} props.stickyHeader - Enable sticky header
 */
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  selectable = false,
  selected = [],
  onSelectChange,
  onRowClick,
  /** Intent, not action: fired on sustained pointer hover or keyboard focus of a row (row | null). */
  onRowIntent,
  rowActions = [],
  sortBy = '',
  sortOrder = 'asc',
  onSortChange,
  page = 0,
  rowsPerPage = 10,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  emptyMessage,
  stickyHeader = true,
}) => {
  const theme = useTheme();
  // Row hover is CSS (`&:hover .dt-row-actions`), NOT state. A `hoveredRow` state
  // used to re-render EVERY row and cell on each mouseenter/mouseleave just to change
  // the action icons' opacity — measured on the infrastructure host list (50 rows,
  // two sparklines each): each pointer move cost ~27% of a full table mount.

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = rows.map((row) => row.id);
      onSelectChange?.(allIds);
    } else {
      onSelectChange?.([]);
    }
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }

    onSelectChange?.(newSelected);
  };

  const handleSort = (columnId) => {
    const isAsc = sortBy === columnId && sortOrder === 'asc';
    onSortChange?.(columnId, isAsc ? 'desc' : 'asc');
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Show loading skeleton
  if (loading && rows.length === 0) {
    return (
      <Paper>
        <TableSkeleton rows={rowsPerPage} columns={columns.length} />
      </Paper>
    );
  }

  // Show empty state
  if (!loading && rows.length === 0) {
    return (
      <Paper>
        <EmptyState
          type="no-results"
          title={emptyMessage || 'No data available'}
          description="Try adjusting your filters or search criteria"
        />
      </Paper>
    );
  }

  return (
    <Paper elevation={0} variant="outlined">
      <TableContainer>
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < rows.length
                    }
                    checked={rows.length > 0 && selected.length === rows.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
              )}
              {columns.map((column) => {
                const columnId = column.id || column.field;
                const columnLabel = column.label || column.headerName;
                return (
                  <TableCell
                    key={columnId}
                    align={column.align || 'left'}
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column.sortable && onSortChange ? (
                      <TableSortLabel
                        active={sortBy === columnId}
                        direction={sortBy === columnId ? sortOrder : 'asc'}
                        onClick={() => handleSort(columnId)}
                      >
                        {columnLabel}
                        {sortBy === columnId && (
                          <Box component="span" sx={visuallyHidden}>
                            {sortOrder === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        )}
                      </TableSortLabel>
                    ) : (
                      columnLabel
                    )}
                  </TableCell>
                );
              })}
              {rowActions.length > 0 && (
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isItemSelected = isSelected(row.id);

              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isItemSelected}
                  onMouseEnter={onRowIntent ? () => onRowIntent(row) : undefined}
                  onMouseLeave={onRowIntent ? () => onRowIntent(null) : undefined}
                  onFocus={(e) => { if (e.target === e.currentTarget) onRowIntent?.(row); }}
                  onBlur={(e) => { if (e.target === e.currentTarget) onRowIntent?.(null); }}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row, e); }
                  } : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
                    '&:hover .dt-row-actions': { opacity: 1 },
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => handleSelectRow(row.id)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    const columnId = column.id || column.field;
                    const cellValue = row[columnId];

                    // Support both render and renderCell formats
                    let cellContent;
                    if (column.renderCell) {
                      // MUI DataGrid format: renderCell({ value, row })
                      cellContent = column.renderCell({ value: cellValue, row });
                    } else if (column.render) {
                      // Standard format: render(value, row)
                      cellContent = column.render(cellValue, row);
                    } else {
                      cellContent = cellValue;
                    }

                    return (
                      <TableCell
                        key={columnId}
                        align={column.align || 'left'}
                        onClick={(e) => onRowClick?.(row, e)}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                  {rowActions.length > 0 && (
                    <TableCell align="right">
                      <Box
                        className="dt-row-actions"
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'flex-end',
                          opacity: 0.5,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        {rowActions.map((action, actionIndex) => {
                          const icon = typeof action.icon === 'function' ? action.icon(row) : action.icon;
                          if (action.renderRaw) {
                            return (
                              <Box key={actionIndex} onClick={(e) => e.stopPropagation()}>
                                {icon}
                              </Box>
                            );
                          }
                          return (
                            <Tooltip key={actionIndex} title={action.label}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(row, e);
                                }}
                              >
                                {icon}
                              </IconButton>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {(onPageChange || onRowsPerPageChange) && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalRows || rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(event) =>
            onRowsPerPageChange?.(parseInt(event.target.value, 10))
          }
          labelDisplayedRows={({ from, to, count }) => {
            const formattedFrom = from.toLocaleString();
            const formattedTo = to.toLocaleString();
            const formattedCount = count.toLocaleString();
            return count !== -1
              ? `${formattedFrom}-${formattedTo} of ${formattedCount}`
              : `${formattedFrom}-${formattedTo} of more than ${formattedTo}`;
          }}
        />
      )}
    </Paper>
  );
};

export default DataTable;
